import type { DisplayJob } from '../types';
import type { SearchEntry } from '../../redux/features/savedSearchesSlice';
import type { ComparisonPreferencesState } from '../../redux/features/comparisonPreferencesSlice';
import type { ComparisonWeights } from '../data/job-comparison';
import type { MatchScoreBreakdown } from '../data/calculate-job-match-score';
import type { SearchInferenceBullet } from '../../types/job-match';
import { mergeFacetValuesWithRecencyConflictBias } from './merge-facet-values-with-recency-conflict-bias';
import {
  scoreJobAgainstWeightedFacetValues,
} from './score-job-against-facet-refinement';
import { coerceToStringArray } from './coerce-to-string-array';
import { prepareWeightedSearchSummariesForInference } from './prepare-weighted-search-summaries-for-inference';
import {
  RECENT_SEARCH_LOCATION_KEY,
  collectAllCanonicalFacetKeys,
  normalizeSearchLocationToken,
  snapshotsForCanonicalPrepared,
} from './recent-search-facet-snapshots';

const ROLE_ATTR = new Set([
  'jobtype',
  'field',
  'normalisedtitle',
  'normalisedroletitle',
  'rolelevel',
  'normalisedrolelevel',
]);

function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .split(/[^a-z0-9åäöüÅÄÖÜ]+/)
    .filter((t) => t.length >= 2)
    .slice(0, 24);
}

function jobLocationHaystack(job: DisplayJob): string[] {
  return coerceToStringArray(job.location).map(normalizeSearchLocationToken).filter(Boolean);
}

function scoreKeywords(job: DisplayJob, weightedQueries: Array<{ text: string; w: number }>): number {
  if (weightedQueries.length === 0) return 50;
  const hay = `${job.title ?? ''} ${job.normalisedTitle ?? ''} ${job.companyId ?? ''} ${job.field ?? ''} ${job.normalisedRoleLevel ?? ''}`.toLowerCase();
  const termWeights = new Map<string, number>();
  for (const { text, w } of weightedQueries) {
    for (const t of tokenize(text)) {
      termWeights.set(t, (termWeights.get(t) ?? 0) + w);
    }
  }
  if (termWeights.size === 0) return 50;
  let matched = 0;
  let total = 0;
  for (const [term, tw] of termWeights) {
    total += tw;
    if (hay.includes(term)) matched += tw;
  }
  if (total <= 0) return 50;
  return Math.max(0, Math.min(100, Math.round((matched / total) * 100)));
}

function scoreLocations(job: DisplayJob, weightedLocs: Array<{ loc: string; w: number }>): number {
  if (weightedLocs.length === 0) return 50;
  const jl = jobLocationHaystack(job);
  if (jl.length === 0) return 40;
  let sum = 0;
  let wsum = 0;
  for (const { loc, w } of weightedLocs) {
    const n = normalizeSearchLocationToken(loc);
    wsum += w;
    const hit = jl.some((j) => j.includes(n) || n.includes(j) || tokenize(n).some((p) => p.length > 3 && j.includes(p)));
    sum += (hit ? 100 : 15) * w;
  }
  return wsum > 0 ? Math.max(0, Math.min(100, Math.round(sum / wsum))) : 50;
}

function scoreRemoteness(job: DisplayJob, weights: Map<string, number>): number {
  if (weights.size === 0) return 50;
  const jr = String(job.remoteness ?? '').toLowerCase().trim();
  if (!jr) return 45;
  let sum = 0;
  let wsum = 0;
  for (const [v, w] of weights) {
    wsum += w;
    const lv = v.toLowerCase().trim();
    sum += (jr === lv ? 100 : jr.includes('hybrid') && lv.includes('hybrid') ? 80 : 30) * w;
  }
  return wsum > 0 ? Math.max(0, Math.min(100, Math.round(sum / wsum))) : 50;
}

function scoreRoleFit(
  job: DisplayJob,
  weightedFacetValues: Array<{ value: string; w: number }>
): number {
  if (weightedFacetValues.length === 0) return 50;
  const hay = `${job.jobType ?? ''} ${job.field ?? ''} ${job.normalisedTitle ?? ''} ${job.title ?? ''}`.toLowerCase();
  let sum = 0;
  let wsum = 0;
  for (const { value, w } of weightedFacetValues) {
    const v = value.toLowerCase().trim();
    if (!v) continue;
    wsum += w;
    const hit = hay.includes(v) || tokenize(v).some((t) => t.length > 3 && hay.includes(t));
    sum += (hit ? 100 : 20) * w;
  }
  return wsum > 0 ? Math.max(0, Math.min(100, Math.round(sum / wsum))) : 50;
}

function scoreEmployer(job: DisplayJob, companyWeights: Map<string, number>, industryWeights: Map<string, number>): number {
  const parts: Array<{ score: number; w: number }> = [];
  const cid = String(job.companyId ?? '').toLowerCase().trim();
  if (companyWeights.size > 0 && cid) {
    let cw = 0;
    let sw = 0;
    for (const [c, w] of companyWeights) {
      sw += w;
      cw += (cid.includes(c.toLowerCase()) || c.toLowerCase().includes(cid) ? 100 : 10) * w;
    }
    if (sw > 0) parts.push({ score: Math.round(cw / sw), w: 1 });
  }
  const ind = String(job.companyIndustry ?? '').toLowerCase().trim();
  if (industryWeights.size > 0 && ind) {
    let cw = 0;
    let sw = 0;
    for (const [c, w] of industryWeights) {
      sw += w;
      cw += (ind.includes(c.toLowerCase()) || c.toLowerCase().includes(ind) ? 100 : 15) * w;
    }
    if (sw > 0) parts.push({ score: Math.round(cw / sw), w: 1 });
  }
  if (parts.length === 0) return 50;
  const t = parts.reduce((a, p) => a + p.score * p.w, 0) / parts.reduce((a, p) => a + p.w, 0);
  return Math.max(0, Math.min(100, Math.round(t)));
}

function isGenericFacetKey(canonical: string): boolean {
  if (canonical === RECENT_SEARCH_LOCATION_KEY) return false;
  if (canonical === 'remoteness') return false;
  if (canonical === 'companyid' || canonical === 'industry') return false;
  if (ROLE_ATTR.has(canonical)) return false;
  return true;
}

function sumMapValues(m: Map<string, number>): number {
  let s = 0;
  for (const w of m.values()) s += w;
  return s;
}

function combineScores(a: number, wa: number, b: number, wb: number): number {
  if (wa <= 0 && wb <= 0) return 50;
  if (wa <= 0) return b;
  if (wb <= 0) return a;
  return Math.max(0, Math.min(100, Math.round((a * wa + b * wb) / (wa + wb))));
}

export type InferJobMatchFromRecentSearchesResult = {
  breakdown: MatchScoreBreakdown;
  bullets: SearchInferenceBullet[];
};

/**
 * When the user has no profile match data, infers a 0–100 match from persisted recent search URLs
 * and explains which signals were used. Recency (timestamp + order) shapes weights; conflicting
 * facet refinements across searches down-rank older, disjoint choices.
 */
export function inferJobMatchFromRecentSearches(
  job: DisplayJob,
  entries: SearchEntry[],
  prefs: ComparisonPreferencesState,
  options?: { nowMs?: number }
): InferJobMatchFromRecentSearchesResult | null {
  const prepared = prepareWeightedSearchSummariesForInference(entries, { nowMs: options?.nowMs });
  if (!prepared?.length) return null;

  const allKeys = collectAllCanonicalFacetKeys(prepared);

  const mergedLocation = mergeFacetValuesWithRecencyConflictBias(
    snapshotsForCanonicalPrepared(prepared, RECENT_SEARCH_LOCATION_KEY)
  );
  const weightedLocs = [...mergedLocation.entries()].map(([loc, w]) => ({ loc, w }));

  const mergedRemoteness = mergeFacetValuesWithRecencyConflictBias(
    snapshotsForCanonicalPrepared(prepared, 'remoteness')
  );

  const mergedCompany = mergeFacetValuesWithRecencyConflictBias(
    snapshotsForCanonicalPrepared(prepared, 'companyid')
  );
  const mergedIndustry = mergeFacetValuesWithRecencyConflictBias(
    snapshotsForCanonicalPrepared(prepared, 'industry')
  );

  const roleWeighted: Array<{ value: string; w: number }> = [];
  for (const roleKey of ROLE_ATTR) {
    if (!allKeys.includes(roleKey)) continue;
    const merged = mergeFacetValuesWithRecencyConflictBias(snapshotsForCanonicalPrepared(prepared, roleKey));
    for (const [value, w] of merged) {
      if (w > 0) roleWeighted.push({ value, w });
    }
  }

  let genericScoreSum = 0;
  let genericWeightSum = 0;
  for (const key of allKeys) {
    if (!isGenericFacetKey(key)) continue;
    const merged = mergeFacetValuesWithRecencyConflictBias(snapshotsForCanonicalPrepared(prepared, key));
    const wTot = sumMapValues(merged);
    if (wTot <= 0) continue;
    const sub = scoreJobAgainstWeightedFacetValues(job, key, merged);
    genericScoreSum += sub * wTot;
    genericWeightSum += wTot;
  }
  const genericScore = genericWeightSum > 0 ? Math.round(genericScoreSum / genericWeightSum) : 50;

  const weightedQueries: Array<{ text: string; w: number }> = [];
  for (const p of prepared) {
    const q = p.summary.query?.trim();
    if (q && q.length >= 2) {
      weightedQueries.push({ text: q, w: p.w });
    }
  }

  if (
    weightedQueries.length === 0 &&
    weightedLocs.length === 0 &&
    mergedRemoteness.size === 0 &&
    mergedCompany.size === 0 &&
    mergedIndustry.size === 0 &&
    roleWeighted.length === 0 &&
    genericWeightSum <= 0
  ) {
    return null;
  }

  const wPref: ComparisonWeights = {
    salaryWeight: prefs.salaryWeight ?? 0.2,
    vacationWeight: prefs.vacationWeight ?? 0.2,
    benefitsWeight: prefs.benefitsWeight ?? 0.2,
    remotenessWeight: prefs.remotenessWeight ?? 0.2,
    languagesWeight: prefs.languagesWeight ?? 0.2,
  };

  const keywordScore = scoreKeywords(job, weightedQueries);
  const locationScore = scoreLocations(job, weightedLocs);
  const remotenessScore = scoreRemoteness(job, mergedRemoteness);
  const roleOnlyScore = scoreRoleFit(job, roleWeighted);
  const roleWeightTotal = roleWeighted.reduce((a, r) => a + r.w, 0);
  const roleScore = combineScores(roleOnlyScore, roleWeightTotal, genericScore, genericWeightSum);
  const employerScore = scoreEmployer(job, mergedCompany, mergedIndustry);

  let overall =
    keywordScore * wPref.salaryWeight +
    locationScore * wPref.vacationWeight +
    roleScore * wPref.benefitsWeight +
    remotenessScore * wPref.remotenessWeight +
    employerScore * wPref.languagesWeight;

  if (!isFinite(overall) || isNaN(overall)) overall = 0;
  overall = Math.max(0, Math.min(100, Math.round(overall)));

  const breakdown: MatchScoreBreakdown = {
    overall,
    salary: keywordScore,
    vacation: locationScore,
    benefits: roleScore,
    remoteness: remotenessScore,
    languages: employerScore,
    weights: wPref,
  };

  const bullets: SearchInferenceBullet[] = [];

  const topQueries = weightedQueries.slice(0, 3).map((q) => q.text);
  const uniqueQuerySnippet = Array.from(new Set(topQueries)).slice(0, 2).join(', ');
  if (weightedQueries.length > 0 && uniqueQuerySnippet) {
    bullets.push({ type: 'keywords', terms: uniqueQuerySnippet });
  }

  const locExamples = Array.from(mergedLocation.keys()).slice(0, 3).join(', ');
  if (weightedLocs.length > 0 && locExamples) {
    bullets.push({ type: 'locations', locations: locExamples });
  }

  const remExamples = Array.from(mergedRemoteness.keys()).slice(0, 3).join(', ');
  if (mergedRemoteness.size > 0 && remExamples) {
    bullets.push({ type: 'remoteness', values: remExamples });
  }

  const roleHints = Array.from(new Set(roleWeighted.map((r) => r.value))).slice(0, 4).join(', ');
  if (roleWeighted.length > 0 && roleHints) {
    bullets.push({ type: 'roles', hints: roleHints });
  }

  const companyHint = job.companyId
    ? Array.from(mergedCompany.keys()).find((c) => c.toLowerCase() === String(job.companyId).toLowerCase()) ?? job.companyId
    : Array.from(mergedCompany.keys())[0];
  if (mergedCompany.size > 0 && companyHint) {
    bullets.push({ type: 'company', company: companyHint });
  }

  return { breakdown, bullets: bullets.slice(0, 5) };
}
